import { useNavigate, useLocation } from "react-router-dom";

import { Button } from "@ogt/ui";
import { TrafficBadge } from "@ogt/ui";
import { cn } from "@ogt/ui";
import { Heading } from "@ogt/ui";
import { Icon } from "@ogt/ui";

interface JourneyStopPointData {
  stopPointAlias?: string | null;
}

interface JourneyPlace {
  PlaceName?: string;
  OgtStopPointData?: JourneyStopPointData | null;
}

interface RouteLink {
  RouteLinkKey?: string;
  DepDateTime?: string;
  ArrDateTime?: string;
  From?: JourneyPlace;
  To?: JourneyPlace;

  Line: {
    LineName: string;
    LineTypeName: string;
    RunNo?: number;
    TrainNo?: number;
    OperatorId?: string;
    IsWalk?: boolean;

    Ogt: {
      OgtLineNamePreformatted?: string;
      OgtTrafficTypeGroupCssClass: string;
      OgtTrafficTypeIconCssClass: string;
      OgtTrafficTypeIconReplaceLineName: boolean;
    };
  };
}

interface JourneyProps {
  journey: {
    Headline: string;
    from: string;
    to: string;
    date: string;
    travelTime: string;
    depatureArrivalTime: string;
    switches: string | number;
    line: RouteLink[];
    editPath: string;
  };
  isReturnTravel: boolean;
  showEditButton?: boolean;
  showRemoveButton?: boolean;
  onEdit?: (editPath: string) => void;
  onRemove?: () => void;
}

type TrafficMeta = Parameters<typeof TrafficBadge>[0]["meta"];

function toTrafficMeta(link: RouteLink): TrafficMeta {
  return {
    iconCss: link.Line.Ogt.OgtTrafficTypeIconCssClass,
    groupCss: link.Line.Ogt.OgtTrafficTypeGroupCssClass,
    title: link.Line.LineName,
  };
}

function formatJourneyTime(value?: string) {
  return value?.match(/T(\d{2}:\d{2})/)?.[1] ?? "";
}

function getRouteColor(link: RouteLink): string {
  const group =
    link.Line?.Ogt?.OgtTrafficTypeGroupCssClass?.toUpperCase() ?? "";

  switch (group) {
    case "LAND":
      return "var(--badge-color-bg-green)";

    case "TÅG":
      return "var(--badge-color-bg-orange)";

    case "TÄTO":
      return "var(--badge-color-bg-red)";

    case "EXPRESS":
      return "var(--badge-color-bg-red)";

    case "NÄRTRAFIK":
      return "var(--badge-color-bg-blue)";

    case "BÅT":
      return "var(--badge-color-bg-blue)";

    default:
      return "var(--badge-color-bg-grey)";
  }
}

function getStopPointLabel(place: JourneyPlace | undefined, link: RouteLink) {
  const alias = place?.OgtStopPointData?.stopPointAlias;

  if (!alias) return null;

  const isTrain =
    link.Line.Ogt.OgtTrafficTypeGroupCssClass.toUpperCase() === "TÅG" ||
    link.Line.LineTypeName.toLowerCase().includes("tåg");

  return `${isTrain ? "Spår" : "Läge"} ${alias.toUpperCase()}`;
}

function getServiceIdentity(link: RouteLink) {
  const { TrainNo, RunNo, OperatorId, LineName } = link.Line;

  if (TrainNo) {
    return `train:${OperatorId ?? ""}:${TrainNo}`;
  }

  if (RunNo) {
    return `run:${OperatorId ?? ""}:${RunNo}`;
  }

  return `line:${OperatorId ?? ""}:${LineName}`;
}

function getTransferMinutes(current: RouteLink, next: RouteLink) {
  if (getServiceIdentity(current) === getServiceIdentity(next)) {
    return null;
  }

  if (!current.ArrDateTime || !next.DepDateTime) {
    return null;
  }

  const arrival = new Date(current.ArrDateTime).getTime();
  const departure = new Date(next.DepDateTime).getTime();
  const minutes = Math.round((departure - arrival) / 60_000);

  return minutes >= 0 ? minutes : null;
}

function JourneyTimeline({ links }: { links: RouteLink[] }) {
  return (
    <ol className="m-0 mt-5 list-none p-0">
      {links.map((link, index) => {
        const nextLink = links[index + 1];
        const transferMinutes = nextLink
          ? getTransferMinutes(link, nextLink)
          : null;

        const meta = toTrafficMeta(link);
        const routeColor = getRouteColor(link);

        const lineName =
          link.Line.Ogt.OgtLineNamePreformatted ||
          link.Line.LineName ||
          "Okänd linje";

        const fromStopPoint = getStopPointLabel(link.From, link);
        const toStopPoint = getStopPointLabel(link.To, link);

        return (
          <li key={link.RouteLinkKey ?? index}>
            <div className="relative grid grid-cols-[4.25rem_minmax(0,1fr)] gap-x-3 pl-7">
              <span
                aria-hidden="true"
                className="absolute bottom-2 left-[7px] top-2 w-0.5"
                style={{ backgroundColor: routeColor }}
              />

              <span
                aria-hidden="true"
                className="absolute left-0 top-1 size-4 rounded-full"
                style={{ backgroundColor: routeColor }}
              />

              <time
                dateTime={link.DepDateTime}
                className="col-start-1 row-start-1 font-bold"
              >
                {formatJourneyTime(link.DepDateTime)}
              </time>

              <div className="col-start-2 row-start-1">
                <strong className="block">{link.From?.PlaceName}</strong>

                {fromStopPoint && (
                  <span className="block text-sm text-muted-foreground">
                    {fromStopPoint}
                  </span>
                )}
              </div>

              <div className="col-start-2 row-start-2 my-4 flex items-center gap-2">
                <TrafficBadge
                  meta={meta}
                  className={cn(
                    "lineicon",
                    link.Line.LineTypeName,
                    link.Line.Ogt.OgtTrafficTypeGroupCssClass
                  )}
                  titleOverride={lineName}
                />

                <span>{lineName}</span>
              </div>

              <span
                aria-hidden="true"
                className="absolute bottom-1 left-0 size-4 rounded-full"
                style={{ backgroundColor: routeColor }}
              />

              <time
                dateTime={link.ArrDateTime}
                className="col-start-1 row-start-3 font-bold"
              >
                {formatJourneyTime(link.ArrDateTime)}
              </time>

              <div className="col-start-2 row-start-3">
                <strong className="block">{link.To?.PlaceName}</strong>

                {toStopPoint && (
                  <span className="block text-sm text-muted-foreground">
                    {toStopPoint}
                  </span>
                )}
              </div>
            </div>

            {nextLink && transferMinutes !== null && (
              <div
                className="my-10 flex items-center gap-2 rounded-md bg-muted px-3 py-4 bg-[var(--box-color-bg-light)] radius-[var(--box-border-radius)];
 "
              >
                <span aria-hidden="true">
                  <Icon name="clock" size={20} />
                </span>

                <span>
                  <strong>Bytestid:</strong> {transferMinutes} min
                </span>
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

export default function JourneySummary({
  journey,
  isReturnTravel,
  showEditButton = true,
  showRemoveButton = true,
  onEdit,
  onRemove,
}: JourneyProps) {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <div className="last:mb-0 last:pb-0">
      <Heading as="h3" className="summary-section-heading">
        {journey.Headline}
      </Heading>
      <div className="mt-2 flex flex-col md:flex-row justify-between">
        <div className="flex flex-col gap-2 mt-2">
          <div className="flex gap-2">
            <span className="summary-data-label font-bold">Från:</span>
            <span className="summary-data">{journey.from}</span>
          </div>
          <div className="flex gap-2">
            <span className="summary-data-label font-bold">Till:</span>
            <span className="summary-data">{journey.to}</span>
          </div>
          <div className="flex gap-2">
            <span className="summary-data-label font-bold">
              Datum för resa:
            </span>
            <span className="summary-data">{journey.date}</span>
          </div>
          <div className="flex gap-2">
            <span className="summary-data-label font-bold">
              Avgång- och ankomsttid:
            </span>
            <span className="summary-data">{journey.depatureArrivalTime}</span>
          </div>
          <div className="flex gap-2">
            <span className="summary-data-label font-bold">Restid:</span>
            <span className="summary-data">{journey.travelTime}</span>
          </div>
          <div className="flex gap-2">
            <span className="summary-data-label font-bold">Antal byten:</span>
            <span className="summary-data">
              {journey.switches}{" "}
              {Number(journey.switches) === 1 ? "byte" : "byten"}
            </span>
          </div>
          <div className="flex gap-2">
            <span className="summary-data-label font-bold mb-0">
              Resinformation:
            </span>

            <span className="lineicon-container flex-wrap summary-data mb-0 flex gap-2">
              {Array.isArray(journey.line) && journey.line.length > 0 ? (
                journey.line.map((item, index) => {
                  const meta = toTrafficMeta(item);
                  const isPendeln =
                    item?.Line?.LineName?.includes("Östgötapendeln");
                  const showText =
                    !item?.Line?.Ogt?.OgtTrafficTypeIconReplaceLineName;

                  return (
                    <TrafficBadge
                      key={index}
                      meta={meta}
                      className={cn(
                        "lineicon",
                        item?.Line?.LineTypeName,
                        item?.Line?.Ogt?.OgtTrafficTypeGroupCssClass,
                        isPendeln && "pendeln"
                      )}
                      titleOverride={undefined}
                    >
                      {showText ? item?.Line?.LineName ?? "Okänd linje" : null}
                    </TrafficBadge>
                  );
                })
              ) : (
                <span>Ingen linjeinformation</span>
              )}
            </span>
          </div>
          {Array.isArray(journey.line) && journey.line.length > 0 && (
            <JourneyTimeline links={journey.line} />
          )}
        </div>

        <div className="flex flex-col gap-2">
          {showEditButton && (
            <div className="summary-edit mb-4 md:mb-0 mt-3 md:mt-0">
              <Button
                type="button"
                variant={"secondary"}
                onClick={() => onEdit?.(journey.editPath)}
                iconName="square-pen"
                showIcon
                iconPosition="left"
              >
                Gå tillbaka och ändra
              </Button>
            </div>
          )}
          {isReturnTravel && showRemoveButton && (
            <div className="summary-remove">
              <Button
                type="button"
                variant={"secondary"}
                iconName="trash"
                showIcon
                iconPosition="left"
                onClick={() => onRemove?.()}
              >
                Ta bort
              </Button>
            </div>
          )}
        </div>
      </div>
      <hr />
    </div>
  );
}
